import { ReactNode } from 'react';

import {
  RiCheckboxBlankLine,
  RiCodeBoxLine,
  RiDoubleQuotesL,
  RiH1,
  RiH2,
  RiH3,
  RiH4,
  RiImageLine,
  RiLayout2Line,
  RiLayout4Line,
  RiLayoutColumnLine,
  RiListOrdered2,
  RiListUnordered,
  RiPriceTag3Line,
  RiSeparator,
  RiTable2,
  RiText,
  RiVerifiedBadgeFill,
} from '@remixicon/react';
import { Editor, Range } from '@tiptap/core';

export type BlockCommand = {
  title: string;
  description?: string;
  icon?: ReactNode;
  nodeType?: string;
  command?: (props: { editor: Editor; range: Range }) => void;
  section?: string;
  submenu?: BlockCommand[];
  disabled?: boolean;
  disabledReason?: string;
};

const iconSize = 16;

const headingIcons: Record<number, ReactNode> = {
  1: <RiH1 size={iconSize} />,
  2: <RiH2 size={iconSize} />,
  3: <RiH3 size={iconSize} />,
  4: <RiH4 size={iconSize} />,
};

const createHeadingCommand = (level: number): BlockCommand => ({
  title: `H${level}`,
  description: `Heading ${level}`,
  icon: headingIcons[level],
  nodeType: 'heading',
  command: ({ editor, range }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent({
        type: 'heading',
        attrs: { level },
      })
      .run();
  },
});

export const getAvailableCommands = (editor: Editor): BlockCommand[] => {
  const commands: BlockCommand[] = [];
  const schema = editor.schema;

  const headingNode = schema.nodes.heading;
  if (schema.nodes.paragraph) {
    commands.push({
      title: 'Paragraph',
      description: 'Normal text',
      icon: <RiText size={iconSize} />,
      nodeType: 'paragraph',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'paragraph',
          })
          .run();
      },
    });
  }

  if (headingNode) {
    const headingExtension = editor.extensionManager.extensions.find(
      ext => ext.name === 'heading',
    );
    const levels = (headingExtension?.options?.levels as number[]) ?? [
      1, 2, 3, 4,
    ];

    levels.forEach(level => {
      if (level >= 1 && level <= 6) {
        commands.push(createHeadingCommand(level));
      }
    });
  }

  if (schema.nodes.bulletList) {
    commands.push({
      title: 'Bullet list',
      description: 'Create a bullet list',
      icon: <RiListUnordered size={iconSize} />,
      nodeType: 'bulletList',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    });
  }

  if (schema.nodes.orderedList) {
    commands.push({
      title: 'Numbered list',
      description: 'Create a numbered list',
      icon: <RiListOrdered2 size={iconSize} />,
      nodeType: 'orderedList',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'orderedList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    });
  }

  if (schema.nodes.image) {
    commands.push({
      title: 'Image',
      description: 'Upload and display an image',
      icon: <RiImageLine size={iconSize} />,
      nodeType: 'image',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).setImage({ src: '' }).run();
      },
    });
  }

  if (schema.nodes.calloutBlock) {
    commands.push({
      title: 'Block',
      description: 'Add a highlighted block',
      icon: <RiCheckboxBlankLine size={iconSize} />,
      nodeType: 'calloutBlock',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).toggleCalloutBlock().run();
      },
    });
  }

  if (schema.nodes.blockquote) {
    commands.push({
      title: 'Quote',
      description: 'Add a blockquote',
      icon: <RiDoubleQuotesL size={iconSize} />,
      nodeType: 'blockquote',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'blockquote',
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    });
  }

  if (schema.nodes.horizontalRule) {
    commands.push({
      title: 'Divider',
      description: 'Add a horizontal rule',
      icon: <RiSeparator size={iconSize} />,
      nodeType: 'horizontalRule',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).setHorizontalRule().run();
      },
    });
  }

  if (schema.nodes.columns && schema.nodes.column) {
    commands.push({
      title: '2 Columns',
      description: 'Add a two-column layout',
      icon: <RiLayoutColumnLine size={iconSize} />,
      nodeType: 'columns',
      command: ({ editor: ed, range }) => {
        const insertPos = range.from;
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'columns',
            content: [
              {
                type: 'column',
                content: [{ type: 'paragraph' }],
              },
              {
                type: 'column',
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .setTextSelection(insertPos + 2)
          .run();
      },
    });

    commands.push({
      title: '3 Columns',
      description: 'Add a three-column layout',
      icon: <RiLayout2Line size={iconSize} />,
      nodeType: 'columns',
      command: ({ editor: ed, range }) => {
        const insertPos = range.from;
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'columns',
            content: [
              {
                type: 'column',
                content: [{ type: 'paragraph' }],
              },
              {
                type: 'column',
                content: [{ type: 'paragraph' }],
              },
              {
                type: 'column',
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .setTextSelection(insertPos + 2)
          .run();
      },
    });
  }

  if (schema.nodes.table) {
    commands.push({
      title: 'Table',
      description: 'Insert a table',
      icon: <RiTable2 size={iconSize} />,
      nodeType: 'table',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run();
      },
    });
  }

  if (schema.nodes.codeBlock) {
    commands.push({
      title: 'Code block',
      description: 'Add a code block',
      icon: <RiCodeBoxLine size={iconSize} />,
      nodeType: 'codeBlock',
      command: ({ editor: ed, range }) => {
        ed.chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'codeBlock',
          })
          .run();
      },
    });
  }

  if (schema.nodes.tag) {
    commands.push({
      title: 'Tag',
      description: 'Add an inline tag with icon',
      icon: <RiPriceTag3Line size={iconSize} />,
      nodeType: 'tag',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).setTag().run();
      },
    });
  }

  if (schema.nodes.highlightBlock) {
    commands.push({
      title: 'Highlight',
      description: 'Add a highlighted recommendation',
      icon: <RiVerifiedBadgeFill size={iconSize} />,
      nodeType: 'highlightBlock',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).setHighlight().run();
      },
    });
  }

  if (schema.nodes.imageCard) {
    commands.push({
      title: 'Image card',
      description: 'Add an image card with title and description',
      icon: <RiLayout4Line size={iconSize} />,
      nodeType: 'imageCard',
      command: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).setImageCard().run();
      },
    });
  }

  return commands;
};
